import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { apiService } from "../services/api";

interface WaitlistDialogProps {
  open: boolean;
  onClose: () => void;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "#ffffff",
    fontSize: "16px",
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(226, 114, 91, 0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#E2725B", borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.6)",
    fontSize: "15px",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#E2725B" },
};

const formatPhone = (v: string) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export default function WaitlistDialog({ open, onClose }: WaitlistDialogProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [professionalType, setProfessionalType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleClose = () => {
    if (loading) return;
    setName("");
    setEmail("");
    setPhone("");
    setProfessionalType("");
    setError("");
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const digitsPhone = phone.replace(/\D/g, "");

    if (!trimmedName) {
      setError("Informe seu nome completo.");
      return;
    }
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Informe um email válido.");
      return;
    }
    if (digitsPhone.length < 10) {
      setError("Informe um telefone válido com DDD.");
      return;
    }
    if (professionalType !== "medico" && professionalType !== "dentista") {
      setError("Selecione se você é médico ou dentista.");
      return;
    }

    setLoading(true);
    try {
      await apiService.joinWaitlist({
        name: trimmedName,
        email: trimmedEmail,
        phone: digitsPhone,
        professionalType,
      });
      setSuccess(true);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Não foi possível registrar agora. Tente novamente em instantes.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background:
            "linear-gradient(135deg, rgba(26, 49, 60, 0.95) 0%, rgba(4, 9, 10, 0.95) 100%)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(226, 114, 91, 0.25)",
          borderRadius: "28px",
          color: "#ffffff",
        },
      }}
    >
      <Box sx={{ position: "relative", padding: { xs: "28px", md: "36px" } }}>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.6)",
            "&:hover": { color: "#E2725B" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          sx={{
            color: "#E2725B",
            fontWeight: 700,
            fontSize: "0.85rem",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            marginBottom: "10px",
          }}
        >
          Lista de espera
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: "#ffffff",
            fontWeight: 700,
            fontFamily: "Oxanium, sans-serif",
            letterSpacing: "-0.02em",
            marginBottom: "12px",
          }}
        >
          {success
            ? "Pronto! Você está na lista."
            : "Quero ser avisado quando abrirem novas vagas"}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.7)",
            fontSize: "0.98rem",
            lineHeight: 1.6,
            marginBottom: "24px",
          }}
        >
          {success
            ? "Vamos te avisar por email e WhatsApp assim que as inscrições reabrirem."
            : "As novas assinaturas estão encerradas no momento. Deixe seus dados e te chamamos quando reabrirmos."}
        </Typography>

        {success ? (
          <Button
            onClick={handleClose}
            variant="contained"
            fullWidth
            sx={{
              background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
              borderRadius: "20px",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 700,
              padding: "14px 32px",
              color: "#ffffff",
            }}
          >
            Fechar
          </Button>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Nome completo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              fullWidth
              disabled={loading}
              sx={inputSx}
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              disabled={loading}
              sx={inputSx}
            />
            <TextField
              label="Telefone (com DDD)"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              required
              fullWidth
              disabled={loading}
              placeholder="(11) 99999-9999"
              sx={inputSx}
            />
            <TextField
              select
              label="Você é"
              value={professionalType}
              onChange={(e) => setProfessionalType(e.target.value)}
              required
              fullWidth
              disabled={loading}
              sx={inputSx}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#1a313c",
                      color: "#ffffff",
                    },
                  },
                },
              }}
            >
              <MenuItem value="medico">Médico</MenuItem>
              <MenuItem value="dentista">Dentista</MenuItem>
            </TextField>

            {error && (
              <Alert
                severity="error"
                sx={{
                  backgroundColor: "rgba(204, 59, 42, 0.15)",
                  color: "#ffffff",
                  border: "1px solid rgba(204, 59, 42, 0.4)",
                  borderRadius: "12px",
                  "& .MuiAlert-icon": { color: "#E2725B" },
                }}
              >
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
                borderRadius: "20px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 700,
                padding: "14px 32px",
                color: "#ffffff",
                marginTop: "8px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #CC3B2A 0%, #E2725B 100%)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#ffffff" }} />
              ) : (
                "Entrar na lista de espera"
              )}
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
